import { createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { RCS_CONVERSATION_STATUS_PENDING } from '@rsos/constants/rcsStatuses';
import { rcsApi } from './rcsApi';

const initialState = {
  conversations: {},
  unblurMessageUrls: [],
  // separate tracking for unread messages that won't be affected by API responses
  unreadMessages: {}, // { callerID: boolean }
};

const rcsSlice = createSlice({
  name: 'rcs',
  initialState,
  reducers: {
    addRCSConversation: (state, action) => {
      const payload = action.payload;
      const callerID = payload.caller_id;
      const existingConversation = state.conversations?.[callerID];
      const data = {
        ...existingConversation?.data,
        ...payload,
      };

      // explicitly preserve existing UI state, especially unseenMessage
      const existingUI = existingConversation?.ui || {};

      // preserve unseenMessage - API responses should never clear unread status
      let ui = {
        isRcsToastDismissed: existingUI.isRcsToastDismissed,
        unseenMessage: existingUI.unseenMessage,
      };

      // if unseenMessage was true, preserve it regardless of API response
      if (existingUI.unseenMessage === true) {
        ui.unseenMessage = true;
      }

      if (
        existingConversation &&
        existingConversation.data &&
        existingConversation.data.creation_time !== data.creation_time &&
        data.conversation_status === RCS_CONVERSATION_STATUS_PENDING
      ) {
        // keep the existing UI state intact instead of resetting to preserve unseenMessage
      }
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [callerID]: {
            ...existingConversation,
            data,
            loading: {
              ...existingConversation?.loading,
              getRcsConversationMetadata: false,
            },
            error: {
              ...existingConversation?.error,
              getRcsConversationMetadata: null,
            },
            ui,
          },
        },
      };
    },
    dismissRcsToast: (state, action) => {
      const callerID = action.payload.callerID;
      const existingConversation = state.conversations?.[callerID];
      toast.dismiss(`toastRcsAvailable-${callerID}`);
      if (existingConversation) {
        return {
          ...state,
          conversations: {
            ...state.conversations,
            [callerID]: {
              ...existingConversation,
              ui: {
                isRcsToastDismissed: true,
              },
            },
          },
        };
      }
    },
    setRcsAcceptStatus: (state, action) => {
      const callerID = action.payload.callerID;
      const accept_status = action.payload.accept_status;
      const existingConversation = state.conversations?.[callerID];
      const data = {
        ...existingConversation?.data,
      };
      if (existingConversation) {
        return {
          ...state,
          conversations: {
            ...state.conversations,
            [callerID]: {
              ...existingConversation,
              data: {
                ...data,
                accept_status,
              },
            },
          },
        };
      }
    },
    setRcsUnseenMessageStatus: (state, action) => {
      const callerID = action.payload.callerID;
      const unseenMessage = action.payload.unseenMessage;
      const existingConversation = state.conversations?.[callerID];

      // update the separate unread tracking (this won't be affected by API responses)
      state.unreadMessages[callerID] = unseenMessage;

      if (existingConversation) {
        // only modify the draft state, don't return a new state object
        if (!state.conversations[callerID].ui) {
          state.conversations[callerID].ui = {};
        }
        state.conversations[callerID].ui.unseenMessage = unseenMessage;
      }
    },
    setRcsunblurMessageUrls: (state, action) => {
      const unblurMessageUrls = action.payload.unblurMessageUrls;
      return {
        ...state,
        unblurMessageUrls,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        rcsApi.endpoints.getRcsConversationMetadata.matchFulfilled,
        (state, action) => {
          const originalArgs = action.meta.arg.originalArgs;
          const callerID = originalArgs.callerID;
          const metadata = action.payload;
          const existingConversation = state.conversations?.[callerID];
          const data = {
            ...existingConversation?.data,
            ...metadata,
          };

          // explicitly preserve existing UI state, especially unseenMessage
          const existingUI = existingConversation?.ui || {};

          // preserve unseenMessage - API responses should never clear unread status
          let ui = {
            isRcsToastDismissed: existingUI.isRcsToastDismissed,
            unseenMessage: existingUI.unseenMessage,
          };

          // if unseenMessage was true, preserve it regardless of API response
          if (existingUI.unseenMessage === true) {
            ui.unseenMessage = true;
          }

          if (
            existingConversation &&
            existingConversation.data &&
            existingConversation.data.creation_time !== data.creation_time &&
            data.conversation_status === RCS_CONVERSATION_STATUS_PENDING
          ) {
            // keep the existing UI state intact instead of resetting to preserve unseenMessage
          }
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                data,
                loading: {
                  ...existingConversation?.loading,
                  getRcsConversationMetadata: false,
                },
                error: {
                  ...existingConversation?.error,
                  getRcsConversationMetadata: null,
                },
                ui,
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.getRcsConversationMetadata.matchPending,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  getRcsConversationMetadata: true,
                },
                error: {
                  ...existingConversation?.error,
                  getRcsConversationMetadata: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.getRcsConversationMetadata.matchRejected,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  getRcsConversationMetadata: false,
                },
                error: {
                  ...existingConversation?.error,
                  getRcsConversationMetadata: action.error,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.endRcsConversation.matchFulfilled,
        (state, action) => {
          const originalArgs = action.meta.arg.originalArgs;
          const callerID = originalArgs.callerID;
          const metadata = action.payload;
          const existingConversation = state.conversations?.[callerID];
          const data = {
            ...existingConversation?.data,
            ...metadata,
          };
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                data,
                loading: {
                  ...existingConversation?.loading,
                  endRcsConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  endRcsConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.endRcsConversation.matchPending,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  endRcsConversation: true,
                },
                error: {
                  ...existingConversation?.error,
                  endRcsConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.endRcsConversation.matchRejected,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  endRcsConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  endRcsConversation: action.error,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.acceptRcsConversation.matchFulfilled,
        (state, action) => {
          const originalArgs = action.meta.arg.originalArgs;
          const callerID = originalArgs.callerID;
          const metadata = action.payload;
          const existingConversation = state.conversations?.[callerID];
          const data = {
            ...existingConversation?.data,
            ...metadata,
          };
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                data,
                loading: {
                  ...existingConversation?.loading,
                  acceptRcsConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  acceptRcsConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.acceptRcsConversation.matchPending,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  acceptRcsConversation: true,
                },
                error: {
                  ...existingConversation?.error,
                  acceptRcsConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.acceptRcsConversation.matchRejected,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  acceptRcsConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  acceptRcsConversation: action.error,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.declineRcsConversation.matchFulfilled,
        (state, action) => {
          const originalArgs = action.meta.arg.originalArgs;
          const callerID = originalArgs.callerID;
          const metadata = action.payload;
          const existingConversation = state.conversations?.[callerID];
          const data = {
            ...existingConversation?.data,
            ...metadata,
          };
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                data,
                loading: {
                  ...existingConversation?.loading,
                  declineRcsConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  declineRcsConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.declineRcsConversation.matchPending,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  declineRcsConversation: true,
                },
                error: {
                  ...existingConversation?.error,
                  declineRcsConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.declineRcsConversation.matchRejected,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  declineRcsConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  declineRcsConversation: action.error,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.timeoutRcsConversation.matchFulfilled,
        (state, action) => {
          const originalArgs = action.meta.arg.originalArgs;
          const callerID = originalArgs.callerID;
          const metadata = action.payload;
          const existingConversation = state.conversations?.[callerID];
          const data = {
            ...existingConversation?.data,
            ...metadata,
          };
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                data,
                loading: {
                  ...existingConversation?.loading,
                  timeoutRcsConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  timeoutRcsConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.timeoutRcsConversation.matchPending,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  timeoutRcsConversation: true,
                },
                error: {
                  ...existingConversation?.error,
                  timeoutRcsConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        rcsApi.endpoints.timeoutRcsConversation.matchRejected,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  timeoutRcsConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  timeoutRcsConversation: action.error,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        action => 'emergencyCalls/removeCall'.includes(action.type),
        (state, action) => {
          delete state.conversations[action.payload];
        },
      )
      .addMatcher(
        action => 'emergencyCalls/removeAllCalls'.includes(action.type),
        () => {
          return initialState;
        },
      )
      .addMatcher(
        action => ('LOGOUT_SUCCESS', 'SINATRA_LOGOUT').includes(action.type),
        () => {
          return initialState;
        },
      );
  },
});

const persistConfig = {
  key: 'rcs',
  storage,
};

const persistedReducer = persistReducer(persistConfig, rcsSlice.reducer);

export const {
  dismissRcsToast,
  setRcsAcceptStatus,
  setRcsUnseenMessageStatus,
  setRcsunblurMessageUrls,
  addRCSConversation,
} = rcsSlice.actions;

export default persistedReducer;
